/**
 * DOM overlay for everything that isn't the 3D world. Kept deliberately
 * separate from the renderer so restyling Burnwood is a CSS job.
 */
export type DialogueLine = { speaker: string; color: string; text: string };

const $ = <T extends HTMLElement>(sel: string) => document.querySelector<T>(sel)!;

export class Ui {
  readonly title = $('#title-screen');
  readonly beginButton = $<HTMLButtonElement>('#begin');
  private readonly wipe = $('#wipe');
  private readonly zone = $('#zone-title');
  private readonly dialog = $('#dialogue');
  private readonly dialogName = $('#dialogue-name');
  private readonly dialogText = $('#dialogue-text');
  private readonly prompt = $('#talk-prompt');
  readonly muteButton = $<HTMLButtonElement>('#mute');
  private zoneTimer = 0;
  private typing = 0;
  private typeTimer = 0;
  private fullText = '';
  onLetter: (i: number) => void = () => {};
  onType: () => void = () => {};

  constructor(titleText: string) {
    const word = $('#title-word');
    word.innerHTML = '';
    [...titleText].forEach((ch, i) => {
      const s = document.createElement('span');
      s.className = 'title-letter';
      s.textContent = ch;
      s.style.setProperty('--i', String(i));
      s.style.setProperty('--tilt', `${(Math.sin(i * 12.9898) * 6).toFixed(1)}deg`);
      // Brushing over a letter flips it, a small toy on the title screen.
      s.addEventListener('pointerenter', () => {
        if (s.classList.contains('flip')) return;
        s.classList.add('flip');
        this.onLetter(i);
        setTimeout(() => s.classList.remove('flip'), 700);
      });
      word.appendChild(s);
    });
  }

  hideTitle(): void {
    this.title.classList.add('gone');
  }

  /** Diagonal wipe that covers the screen, runs `mid`, then reveals. */
  async wipeThrough(mid: () => void | Promise<void>): Promise<void> {
    this.wipe.classList.remove('out');
    this.wipe.classList.add('in');
    await wait(650);
    await mid();
    this.wipe.classList.add('out');
    await wait(900);
    this.wipe.classList.remove('in', 'out');
  }

  showZone(name: string): void {
    clearTimeout(this.zoneTimer);
    this.zone.classList.remove('show', 'hide');
    this.zone.innerHTML = '';
    [...name].forEach((ch, i) => {
      const s = document.createElement('span');
      s.textContent = ch === ' ' ? ' ' : ch;
      s.style.setProperty('--i', String(i));
      s.style.setProperty('--tilt', `${(Math.sin((i + name.length) * 7.13) * 7).toFixed(1)}deg`);
      this.zone.appendChild(s);
    });
    void this.zone.offsetWidth;
    this.zone.classList.add('show');
    this.zoneTimer = window.setTimeout(() => this.zone.classList.add('hide'), 3600);
  }

  hideZone(): void {
    this.zone.classList.add('hide');
  }

  get dialogueOpen(): boolean {
    return this.dialog.classList.contains('open');
  }

  get typingDone(): boolean {
    return this.typing >= this.fullText.length;
  }

  showLine(line: DialogueLine): void {
    this.dialog.classList.add('open');
    this.dialogName.textContent = line.speaker;
    this.dialog.style.setProperty('--speaker', line.color);
    this.fullText = line.text;
    this.typing = 0;
    this.dialogText.textContent = '';
    clearInterval(this.typeTimer);
    this.typeTimer = window.setInterval(() => {
      this.typing += 1;
      this.dialogText.textContent = this.fullText.slice(0, this.typing);
      if (this.typing % 2 === 0) this.onType();
      if (this.typing >= this.fullText.length) clearInterval(this.typeTimer);
    }, 1000 / 38);
  }

  finishTyping(): void {
    clearInterval(this.typeTimer);
    this.typing = this.fullText.length;
    this.dialogText.textContent = this.fullText;
  }

  closeDialogue(): void {
    clearInterval(this.typeTimer);
    this.dialog.classList.remove('open');
  }

  setPrompt(x: number, y: number, visible: boolean, label = 'Talk'): void {
    this.prompt.classList.toggle('on', visible);
    if (visible) {
      this.prompt.style.transform = `translate(${x}px, ${y}px) translate(-50%, -100%)`;
      this.prompt.querySelector('span')!.textContent = label;
    }
  }

  setMuted(m: boolean): void {
    this.muteButton.classList.toggle('muted', m);
    this.muteButton.setAttribute('aria-pressed', String(m));
    this.muteButton.setAttribute('aria-label', m ? 'Turn sound on' : 'Turn sound off');
  }
}

export function wait(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
